<!-- Connection Status Icon Component -->
<script setup lang="ts">
import { computed } from 'vue'

interface Props {
  state: 'connected' | 'disconnected' | 'error' | 'checking'
}

const props = withDefaults(defineProps<Props>(), {
  state: 'checking',
})

const iconData = computed(() => {
  switch (props.state) {
    case 'connected':
      return {
        paths: [
          { d: 'M22 11.08V12a10 10 0 1 1-5.93-9.14' },
          { d: 'M22 4 12 14.01 9 11.01', tag: 'polyline' },
        ],
        color: 'var(--color-success)',
        bgColor: 'var(--color-success-light)',
        label: 'Conectado',
      }
    case 'disconnected':
      return {
        paths: [
          { d: 'M12 12m-10 0a10 10 0 1 0 20 0a10 10 0 1 0 -20 0', tag: 'circle', cx: 12, cy: 12, r: 10 },
          { d: 'M15 9 9 15' },
          { d: 'M9 9 15 15' },
        ],
        color: 'var(--color-warning)',
        bgColor: 'var(--color-warning-light)',
        label: 'Desconectado',
      }
    case 'error':
      return {
        paths: [
          { d: 'M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z' },
          { d: 'M12 9 12 13' },
          { d: 'M12 17 12.01 17' },
        ],
        color: 'var(--color-error)',
        bgColor: 'var(--color-error-light)',
        label: 'Error',
      }
    case 'checking':
      return {
        paths: [
          { d: 'M12 12m-10 0a10 10 0 1 0 20 0a10 10 0 1 0 -20 0', tag: 'circle', cx: 12, cy: 12, r: 10, strokeWidth: 3, strokeOpacity: 0.25, fill: 'none' },
          { d: 'M12 2C17.5228 2 22 6.47715 22 12', strokeWidth: 3, strokeLinecap: 'round' },
        ],
        color: 'var(--color-info)',
        bgColor: 'var(--color-info-light)',
        label: 'Verificando...',
        animate: true,
      }
  }
})
</script>

<template>
  <div 
    class="connection-status-icon" 
    :style="{ color: iconData.color, backgroundColor: iconData.bgColor }"
    aria-hidden="true"
  >
    <svg class="connection-status-icon__svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" xmlns="http://www.w3.org/2000/svg">
      <template v-for="path in iconData.paths" :key="path.d">
        <component
          :is="path.tag || 'path'"
          :d="path.d"
          :cx="path.cx"
          :cy="path.cy"
          :r="path.r"
          :stroke-width="path.strokeWidth || 2"
          :stroke-opacity="path.strokeOpacity"
          :fill="path.fill"
          :stroke-linecap="path.strokeLinecap"
        >
          <animateTransform
            v-if="iconData.animate && path.d.startsWith('M12 2')"
            attributeName="transform"
            type="rotate"
            from="0 12 12"
            to="360 12 12"
            dur="1s"
            repeatCount="indefinite"
          />
        </component>
      </template>
    </svg>
  </div>
</template>

<style scoped>
.connection-status-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: var(--radius-full);
  flex-shrink: 0;
}

.connection-status-icon__svg {
  width: 16px;
  height: 16px;
}
</style>